import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowUpDown,
  BookOpen,
  Brain,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  ExternalLink,
  FileText,
  Film,
  FolderOpen,
  Gavel,
  Image as ImageIcon,
  LayoutList,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquare,
  Newspaper,
  Palette,
  Search, Phone } from 'lucide-react';
import { BUSINESSES, BUSINESS_SHORT, HISTORY_KINDS, fetchHistory, fetchRunDetail, signVoicemailPath } from '@/lib/milliData';
import { fetchApproval, fetchDesignPreviews, fetchPostPreviews } from '@/lib/approvalsData';
import { signAssetPaths } from '@/lib/brandData';
import { DEPARTMENTS, departmentById } from '@/lib/milliDepartments';
import ApprovalPreview from '@/components/dashboard/ApprovalPreview';

/* History: everything Milli did, oldest first, in one list (migration 077).
 *
 * The server merges sixteen sources into one row shape and does the
 * searching, filtering and paging; this panel draws the rows and opens
 * them. A run opens to its tool calls and what it produced; anything with
 * an approval card behind it opens to the same preview the inbox draws
 * (pending ones send J.O. to the inbox to decide, since deciding stays in
 * one place); a room, or a chat turn that knows its room (078), opens in
 * the chat; a library file opens in a new tab.
 * Same charcoal, hairlines and mono labels as the ledger beside it. */

const PAGE = 50;

const KIND_ICON = {
  chat: MessageSquare,
  run: Activity,
  room: LayoutList,
  approval: Gavel,
  email: Mail,
  post: Megaphone,
  graphic: ImageIcon,
  flyer: Palette,
  video: Clapperboard,
  blog: Newspaper,
  content: FileText,
  task: ListChecks,
  decision: CheckSquare,
  memory: Brain,
  research: BookOpen,
  asset: Film,
  file: FolderOpen,
  call: Phone,
};

// Kinds whose row can open to an approval preview.
const APPROVAL_KINDS = new Set(['approval', 'email', 'post', 'blog', 'content', 'graphic', 'flyer', 'video']);

const STATUS_DOT = {
  completed: 'bg-emerald-400',
  published: 'bg-emerald-400',
  sent: 'bg-emerald-400',
  approved: 'bg-emerald-400',
  modified: 'bg-emerald-400',
  posted: 'bg-emerald-400',
  ready: 'bg-emerald-400',
  done: 'bg-emerald-400',
  current: 'bg-emerald-400',
  open: 'bg-emerald-400',
  failed: 'bg-red-400',
  rejected: 'bg-red-400',
  cancelled: 'bg-stone-500',
  archived: 'bg-stone-500',
  superseded: 'bg-stone-500',
  expired: 'bg-stone-500',
  pending: 'bg-amber-300',
  queued: 'bg-amber-300',
  running: 'bg-amber-300 animate-pulse',
  generating: 'bg-amber-300 animate-pulse',
  dispatched: 'bg-amber-300 animate-pulse',
  in_progress: 'bg-amber-300',
  undone: 'bg-stone-500',
};

const fmtDay = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};
const fmtWhen = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtUsd = (n) => (n === null || n === undefined ? '' : `$${(Number(n) || 0).toFixed(2)}`);

// Free-text departments (approvals, tasks) meet the 15 desk ids here; a
// match gets the desk's color, anything else stays stone.
const deskFor = (department) => departmentById(String(department || '').trim().toLowerCase().replace(/\s+/g, '_'));

// Every URL hiding in a publisher's results object ({facebook: {permalink}, ...}).
const urlsIn = (value, out = []) => {
  if (!value) return out;
  if (typeof value === 'string') {
    if (/^https?:\/\//i.test(value)) out.push(value);
  } else if (Array.isArray(value)) value.forEach((v) => urlsIn(v, out));
  else if (typeof value === 'object') Object.values(value).forEach((v) => urlsIn(v, out));
  return out;
};

const rowKey = (row) => `${row.kind}:${row.id}`;

const dayStart = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const inputClass =
  'w-full rounded border border-[#3a332a] bg-[#17181a] px-2 py-1.5 font-mono text-[11px] text-stone-200 placeholder:text-stone-600 focus:outline-none focus:border-[color:var(--milli-accent)]';

function Chip({ on, onClick, children, testId, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={on}
      className={`flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${
        on
          ? 'border-[color:var(--milli-accent)] text-[color:var(--milli-accent)] bg-[color:var(--milli-accent)]/10'
          : 'border-[#3a332a] text-stone-500 hover:text-stone-300'
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function LinkButton({ href, onClick, children, testId }) {
  const cls =
    'inline-flex items-center gap-1 rounded border border-[#3a332a] px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-stone-300 hover:border-[color:var(--milli-accent)] hover:text-[color:var(--milli-accent)] transition-colors';
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>
        <ExternalLink size={11} />
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls} data-testid={testId}>
      {children}
    </button>
  );
}

/* The phone call behind a receptionist run (103): who, outcome, the
   transcript as spoken lines, and the voicemail player when one was left. */
function CallDetail({ call, t }) {
  const [audioUrl, setAudioUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    setAudioUrl(null);
    if (call?.recording_path) {
      signVoicemailPath(call.recording_path).then((u) => { if (alive) setAudioUrl(u); });
    }
    return () => { alive = false; };
  }, [call?.recording_path]);
  if (!call) return null;
  const who = [call.caller_name, call.company].filter(Boolean).join(', ') || t('milli.console.history.unknownCaller');
  const lines = Array.isArray(call.transcript) ? call.transcript : [];
  return (
    <div data-testid="milli-history-call">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">
        {t('milli.console.history.call')}
      </div>
      <div className="rounded border border-[#2a2622] bg-[#141517] px-2.5 py-2 text-xs">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-stone-200">{who}</span>
          {call.outcome ? <span className="rounded-full border border-[#3a332a] px-1.5 text-[9px] uppercase tracking-wider text-stone-500">{call.outcome}</span> : null}
          {call.duration_seconds ? <span className="text-[10px] text-stone-600">{call.duration_seconds} s</span> : null}
          {call.callback ? <span className="text-[10px] text-stone-600">{t('milli.console.history.callbackOnFile')}</span> : null}
        </div>
        {call.summary ? <p className="mt-1 text-stone-400 break-words">{call.summary}</p> : null}
        {call.voicemail ? (
          <div className="mt-2 rounded border border-[#3a332a] bg-[#0f1012] px-2 py-1.5" data-testid="milli-history-voicemail">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
              {t('milli.console.history.voicemail')}{call.recording_seconds ? ` · ${call.recording_seconds} s` : ''}
            </div>
            {audioUrl ? (
              <audio controls preload="none" src={audioUrl} className="mt-1 w-full" style={{ maxHeight: 36 }} />
            ) : call.recording_path ? null : (
              <p className="mt-1 text-[11px] text-stone-500">{t('milli.console.history.recordingPending')}</p>
            )}
            <p className="mt-1 text-stone-300 break-words whitespace-pre-wrap">
              {call.recording_transcript || t('milli.console.history.transcriptPending')}
            </p>
          </div>
        ) : null}
        {lines.length ? (
          <ol className="mt-2 space-y-1" data-testid="milli-history-transcript">
            {lines.map((l, i) => (
              <li key={i} className="flex gap-2">
                <span className="w-12 shrink-0 font-mono text-[10px] uppercase text-stone-600">
                  {l.role === 'milli' ? t('milli.console.history.milli') : t('milli.console.history.caller')}
                </span>
                <span className="min-w-0 break-words text-stone-300">{l.text}</span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </div>
  );
}

function RunDetail({ detail, t }) {
  if (!detail?.run) return <p className="text-xs text-stone-500">{t('milli.console.history.noToolCalls')}</p>;
  const calls = Array.isArray(detail.tool_calls) ? detail.tool_calls : [];
  const children = Array.isArray(detail.children) ? detail.children : [];
  return (
    <div className="space-y-3">
      {detail.call ? <CallDetail call={detail.call} t={t} /> : null}
      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">
          {t('milli.console.history.toolCalls')} · {calls.length}
        </div>
        {calls.length === 0 ? (
          <p className="text-xs text-stone-500">{t('milli.console.history.noToolCalls')}</p>
        ) : (
          <ol className="space-y-1.5" data-testid="milli-history-tool-calls">
            {calls.map((c) => (
              <li key={c.id} className="rounded border border-[#2a2622] bg-[#141517] px-2.5 py-1.5 text-xs">
                <div className="flex items-center gap-2 font-mono text-[11px]">
                  <span className="min-w-0 truncate text-stone-200">{c.tool_name}</span>
                  {c.authority_level !== null && c.authority_level !== undefined ? (
                    <span className="shrink-0 whitespace-nowrap rounded-full border border-[#3a332a] px-1.5 text-[9px] uppercase tracking-wider text-stone-500">
                      {t('milli.console.history.authority', { n: c.authority_level })}
                    </span>
                  ) : null}
                  <span className="ml-auto shrink-0 whitespace-nowrap text-[10px] text-stone-600">{fmtTime(c.created_at)}</span>
                </div>
                {c.input_summary ? <p className="mt-1 text-stone-400 break-words">{c.input_summary}</p> : null}
                {c.result_summary ? <p className="mt-0.5 text-stone-500 break-words">{c.result_summary}</p> : null}
              </li>
            ))}
          </ol>
        )}
      </div>
      {children.length ? (
        <div>
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">
            {t('milli.console.history.produced')}
          </div>
          <ul className="flex flex-wrap gap-1.5" data-testid="milli-history-children">
            {children.map((ch) => {
              const Icon = KIND_ICON[ch.kind] || FileText;
              return (
                <li
                  key={`${ch.kind}:${ch.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-[#3a332a] px-2 py-0.5 text-[11px] text-stone-300"
                  title={ch.status || ''}
                >
                  <Icon size={11} className="text-stone-500" />
                  <span className="max-w-[220px] truncate">{ch.title}</span>
                  {ch.status ? <span className="font-mono text-[9px] uppercase text-stone-500">{ch.status}</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ApprovalDetail({ detail, row, onOpenInbox, t }) {
  const approval = detail?.approval;
  const preview = detail?.postPreview;
  const shots = Array.isArray(detail?.shots) ? detail.shots : [];
  const permalinks = row.kind === 'post' ? urlsIn(row.ref?.results) : [];
  const url = row.ref?.url && /^https?:\/\//i.test(row.ref.url) ? row.ref.url : null;
  const pending = approval?.status === 'pending';
  return (
    <div className="space-y-3">
      {preview ? (
        <div className="rounded-md border border-[#2a2622] bg-[#141517] p-2.5">
          <ApprovalPreview preview={preview} t={t} />
        </div>
      ) : null}
      {shots.length ? (
        <div className="grid grid-cols-2 gap-2" data-testid="milli-history-shots">
          {shots.map((s) => (
            <a
              key={s.path}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block overflow-hidden rounded-md border border-[#2a2622] bg-[#141517]"
            >
              <img src={s.url} alt="" loading="lazy" className="block aspect-square w-full object-contain bg-[#1e2023]" />
            </a>
          ))}
        </div>
      ) : null}
      {!preview && !shots.length ? (
        <p className="text-xs text-stone-500">{t('milli.console.history.noPreview')}</p>
      ) : null}
      {approval ? (
        <div className="text-xs text-stone-400">
          {approval.decision_needed ? <p className="text-stone-300">{approval.decision_needed}</p> : null}
          {approval.decided_at ? (
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-stone-500">
              {t('milli.console.history.decided', { when: fmtWhen(approval.decided_at) })} · {approval.status}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {pending ? (
          <LinkButton onClick={onOpenInbox} testId="milli-history-decide">
            {t('milli.console.history.decideInInbox')}
          </LinkButton>
        ) : null}
        {(permalinks.length ? permalinks : url ? [url] : []).map((u) => (
          <LinkButton key={u} href={u}>
            {t('milli.console.history.openLink')}
          </LinkButton>
        ))}
      </div>
    </div>
  );
}

function HistoryRow({ row, expanded, detail, onToggle, onOpenRoom, onOpenInbox, onOpenAsset, t }) {
  const Icon = KIND_ICON[row.kind] || FileText;
  const desk = deskFor(row.department);
  const color = desk?.hex;
  const status = row.status ? String(row.status) : '';
  const kindLabel = t(`milli.console.history.kinds.${row.kind}`);
  const key = rowKey(row);
  return (
    <li className="border-b border-[#2a2622] last:border-b-0" data-testid={`milli-history-row-${row.kind}-${row.id}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-2.5 px-1.5 py-2 text-left hover:bg-[#1a1b1d] transition-colors"
      >
        <span className="mt-0.5 shrink-0 font-mono text-[10px] text-stone-500 w-[58px] whitespace-nowrap">{fmtTime(row.at)}</span>
        <Icon size={14} className="mt-0.5 shrink-0" style={{ color: color || '#78716c' }} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider">
            <span style={{ color: color || undefined }} className={color ? '' : 'text-stone-500'}>
              {kindLabel}
            </span>
            {desk ? <span className="text-stone-600 truncate">{desk.name}</span> : row.department ? (
              <span className="text-stone-600 truncate">{row.department}</span>
            ) : null}
            {row.business ? <span className="hidden sm:inline text-stone-600 truncate">{BUSINESS_SHORT[row.business] ?? row.business}</span> : null}
          </span>
          <span className={`block text-xs text-stone-200 ${expanded ? '' : 'truncate'}`}>{row.title}</span>
          {row.subtitle ? (
            <span className={`block text-[11px] text-stone-500 ${expanded ? 'break-words' : 'truncate'}`}>{row.subtitle}</span>
          ) : null}
        </span>
        <span className="ml-1 flex shrink-0 flex-col items-end gap-1">
          {status ? (
            <span className="flex items-center gap-1 font-mono text-[10px] lowercase text-stone-500" title={status}>
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[status] || 'bg-stone-500'}`} />
              {status}
            </span>
          ) : null}
          {row.cost_usd !== null && row.cost_usd !== undefined ? (
            <span className="font-mono text-[10px] text-stone-600">{fmtUsd(row.cost_usd)}</span>
          ) : null}
          <ChevronDown size={12} className={`text-stone-600 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {expanded ? (
        <div className="px-1.5 pb-3 sm:pl-[86px]" data-testid={`milli-history-detail-${key}`}>
          {detail?.loading ? (
            <p className="text-xs text-stone-500 animate-pulse">…</p>
          ) : detail?.error ? (
            <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{detail.error}</p>
          ) : row.kind === 'chat' || row.kind === 'run' ? (
            <div className="space-y-3">
              <RunDetail detail={detail?.run} t={t} />
              {row.ref?.session_id ? (
                <LinkButton onClick={() => onOpenRoom(row.ref.session_id)} testId="milli-history-open-room">
                  {t('milli.console.history.openRoom')}
                  {row.ref.room_title ? <span className="normal-case tracking-normal text-stone-500">· {row.ref.room_title}</span> : null}
                </LinkButton>
              ) : null}
            </div>
          ) : APPROVAL_KINDS.has(row.kind) && row.ref?.approval_id ? (
            <ApprovalDetail detail={detail} row={row} onOpenInbox={onOpenInbox} t={t} />
          ) : row.kind === 'room' ? (
            <div className="flex flex-wrap items-center gap-2">
              {row.ref?.turn_count !== undefined ? (
                <span className="font-mono text-[10px] text-stone-500">{row.ref.turn_count} {t('milli.console.runs')}</span>
              ) : null}
              <LinkButton onClick={() => onOpenRoom(row.ref?.session_id)} testId="milli-history-open-room">
                {t('milli.console.history.openRoom')}
              </LinkButton>
            </div>
          ) : row.kind === 'asset' ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] text-stone-500">{row.ref?.asset_kind}</span>
              <LinkButton onClick={() => onOpenAsset(row)} testId="milli-history-open-file">
                {t('milli.console.history.openFile')}
              </LinkButton>
            </div>
          ) : (row.kind === 'research' || row.kind === 'file') && row.ref?.url ? (
            <div className="space-y-2">
              {row.kind === 'file' && row.subtitle ? <p className="text-xs text-stone-400 break-words">{row.subtitle}</p> : null}
              <LinkButton href={row.ref.url}>{t('milli.console.history.openLink')}</LinkButton>
            </div>
          ) : (
            <p className="text-xs text-stone-400 break-words">{row.subtitle || row.title}</p>
          )}
        </div>
      ) : null}
    </li>
  );
}

const EMPTY_FILTERS = { q: '', kinds: [], department: '', business: '', from: '', to: '', newestFirst: false };

export default function ConsoleHistory({ chat, onOpenChat, onOpenInbox, refreshTick = 0, t }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ rows: [], total: 0, loading: true, error: null });
  const [expandedKey, setExpandedKey] = useState(null);
  const [details, setDetails] = useState({});

  // Typing waits 300 ms before it becomes a query.
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((f) => (f.q === q ? f : { ...f, q }));
      setOffset(0);
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const setFilter = useCallback((patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setOffset(0);
  }, []);

  const toggleKind = (kind) =>
    setFilter({ kinds: filters.kinds.includes(kind) ? filters.kinds.filter((k) => k !== kind) : [...filters.kinds, kind] });

  const hasFilters = useMemo(
    () => !!(filters.q || filters.kinds.length || filters.department || filters.business || filters.from || filters.to),
    [filters]
  );

  useEffect(() => {
    let cancelled = false;
    setData((d) => ({ ...d, loading: true, error: null }));
    const after = dayStart(filters.from);
    const beforeDay = dayStart(filters.to);
    const before = beforeDay ? new Date(beforeDay.getTime() + 24 * 60 * 60 * 1000) : null;
    fetchHistory({
      after: after ? after.toISOString() : null,
      before: before ? before.toISOString() : null,
      kinds: filters.kinds,
      department: filters.department,
      business: filters.business,
      q: filters.q,
      limit: PAGE,
      offset,
      newestFirst: filters.newestFirst,
    })
      .then(({ rows, total }) => {
        if (!cancelled) setData({ rows, total, loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) setData((d) => ({ ...d, loading: false, error: e?.message || t('milli.console.history.loadError') }));
      });
    return () => {
      cancelled = true;
    };
  }, [filters, offset, refreshTick, t]);

  const loadDetail = useCallback(
    async (row) => {
      const key = rowKey(row);
      setDetails((d) => ({ ...d, [key]: { loading: true } }));
      try {
        if (row.kind === 'chat' || row.kind === 'run') {
          const run = await fetchRunDetail(row.id);
          setDetails((d) => ({ ...d, [key]: { loading: false, run } }));
        } else if (APPROVAL_KINDS.has(row.kind) && row.ref?.approval_id) {
          const id = row.ref.approval_id;
          const [approval, posts, shots] = await Promise.all([
            fetchApproval(id).catch(() => null),
            fetchPostPreviews([id]),
            fetchDesignPreviews([id]),
          ]);
          setDetails((d) => ({ ...d, [key]: { loading: false, approval, postPreview: posts[id] || null, shots: shots[id] || [] } }));
        } else {
          setDetails((d) => ({ ...d, [key]: { loading: false } }));
        }
      } catch (e) {
        setDetails((d) => ({ ...d, [key]: { loading: false, error: e?.message || t('milli.console.history.loadError') } }));
      }
    },
    [t]
  );

  const toggleRow = (row) => {
    const key = rowKey(row);
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (!details[key]) loadDetail(row);
  };

  const openRoom = async (sessionId) => {
    if (!sessionId) return;
    await chat?.switchRoom?.(sessionId);
    onOpenChat?.();
  };

  const openAsset = async (row) => {
    const { url, path } = row.ref || {};
    if (url) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    if (!path) return;
    const signed = await signAssetPaths([path]);
    if (signed[path]) window.open(signed[path], '_blank', 'noopener');
  };

  // Rows arrive in order; a heading marks each new day.
  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const row of data.rows) {
      const d = new Date(row.at);
      const day = Number.isNaN(d.getTime()) ? '' : d.toDateString();
      if (day !== lastDay) {
        out.push({ heading: Number.isNaN(d.getTime()) ? '' : fmtDay(d), rows: [] });
        lastDay = day;
      }
      out[out.length - 1].rows.push(row);
    }
    return out;
  }, [data.rows]);

  const from = data.total ? offset + 1 : 0;
  const to = Math.min(offset + data.rows.length, data.total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE < data.total;

  return (
    <section data-testid="milli-console-history" className="flex flex-col">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">
          {t('milli.console.history.title')}
          <span className="ml-2 text-stone-600" data-testid="milli-history-total">{data.total}</span>
        </span>
        <button
          type="button"
          onClick={() => setFilter({ newestFirst: !filters.newestFirst })}
          className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-stone-500 hover:text-[color:var(--milli-accent)] transition-colors"
          data-testid="milli-history-order"
        >
          <ArrowUpDown size={11} />
          {filters.newestFirst ? t('milli.console.history.newestFirst') : t('milli.console.history.oldestFirst')}
        </button>
      </div>

      <div className="space-y-2 px-4 pb-3">
        <div className="relative">
          <Search size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-stone-600" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('milli.console.history.search')}
            className={`${inputClass} pl-7`}
            data-testid="milli-history-search"
          />
        </div>
        <div className="flex flex-wrap gap-1" data-testid="milli-history-kinds">
          <Chip on={filters.kinds.length === 0} onClick={() => setFilter({ kinds: [] })} testId="milli-history-kind-all">
            {t('milli.console.history.allKinds')}
          </Chip>
          {HISTORY_KINDS.map((kind) => {
            const Icon = KIND_ICON[kind];
            return (
              <Chip key={kind} on={filters.kinds.includes(kind)} onClick={() => toggleKind(kind)} testId={`milli-history-kind-${kind}`}>
                <Icon size={10} />
                {t(`milli.console.history.kinds.${kind}`)}
              </Chip>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select
            value={filters.department}
            onChange={(e) => setFilter({ department: e.target.value })}
            className={inputClass}
            data-testid="milli-history-department"
          >
            <option value="">{t('milli.console.history.allDepartments')}</option>
            {Object.values(DEPARTMENTS).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <select
            value={filters.business}
            onChange={(e) => setFilter({ business: e.target.value })}
            className={inputClass}
            data-testid="milli-history-business"
          >
            <option value="">{t('milli.console.history.allBusinesses')}</option>
            {BUSINESSES.map((b) => (
              <option key={b} value={b}>
                {BUSINESS_SHORT[b] ?? b}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">
            <span className="w-8 shrink-0">{t('milli.console.history.from')}</span>
            <input type="date" value={filters.from} onChange={(e) => setFilter({ from: e.target.value })} className={inputClass} data-testid="milli-history-from" />
          </label>
          <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">
            <span className="w-8 shrink-0">{t('milli.console.history.to')}</span>
            <input type="date" value={filters.to} onChange={(e) => setFilter({ to: e.target.value })} className={inputClass} data-testid="milli-history-to" />
          </label>
        </div>
      </div>

      {data.error ? (
        <p className="mx-4 mb-2 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="milli-history-error">
          {data.error}
        </p>
      ) : null}

      <div className="px-4 pb-2">
        {data.loading && data.rows.length === 0 ? (
          <p className="text-xs text-stone-500 animate-pulse">…</p>
        ) : data.rows.length === 0 ? (
          <div className="rounded-lg border border-[#3a332a] px-4 py-6 text-center" data-testid="milli-history-empty">
            <p className="text-xs text-stone-500">{hasFilters ? t('milli.console.history.noMatch') : t('milli.console.history.empty')}</p>
          </div>
        ) : (
          <div className={data.loading ? 'opacity-60 transition-opacity' : 'transition-opacity'} data-testid="milli-history-list">
            {grouped.map((g, i) => (
              <div key={`${g.heading}-${i}`}>
                <div className="sticky top-0 z-10 bg-[#141517] px-1.5 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">
                  {g.heading}
                </div>
                <ul>
                  {g.rows.map((row) => {
                    const key = rowKey(row);
                    return (
                      <HistoryRow
                        key={key}
                        row={row}
                        expanded={expandedKey === key}
                        detail={details[key]}
                        onToggle={() => toggleRow(row)}
                        onOpenRoom={openRoom}
                        onOpenInbox={onOpenInbox}
                        onOpenAsset={openAsset}
                        t={t}
                      />
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {data.total > 0 ? (
        <div className="flex items-center justify-between px-4 pb-5 pt-1 font-mono text-[10px] text-stone-500" data-testid="milli-history-pager">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
            className="flex items-center gap-1 uppercase tracking-wider disabled:opacity-30 hover:text-[color:var(--milli-accent)] transition-colors"
            data-testid="milli-history-prev"
          >
            <ChevronLeft size={11} />
            {t('milli.console.history.prev')}
          </button>
          <span data-testid="milli-history-showing">{t('milli.console.history.showing', { from, to, total: data.total })}</span>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setOffset(offset + PAGE)}
            className="flex items-center gap-1 uppercase tracking-wider disabled:opacity-30 hover:text-[color:var(--milli-accent)] transition-colors"
            data-testid="milli-history-next"
          >
            {t('milli.console.history.next')}
            <ChevronRight size={11} />
          </button>
        </div>
      ) : null}
    </section>
  );
}
